import { Icon } from '@monorepo/base/src/components/icon/icon';
import styles from './sidebar.module.scss';
import { Logo } from '@monorepo/base/src/components/logo/logo';
import { Sidebar, SidebarMode } from '@monorepo/base/src/components/sidebar/sidebar';

interface IControlledSidebarProps {
	onClose?: () => void;
}
import { IControlledStore, useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { useEffect } from 'react';
import routes from '../../routes/index.routes';
import { observer } from 'mobx-react';
import { useRoute } from '@monorepo/tools/src/lib/hooks/tools/use-route';
import { useDevices } from '@monorepo/tools/src/lib/hooks/tools/use-devices';
import { LinkWithParams } from '@monorepo/base/src/components/link-with-params/link-with-params';
import { AdminxLogo } from '../logo/logo';
import React from 'react';
import { SeparationItemList } from './SeparationItemList';
import { SideOpenItemList } from './SideOpenItemList';

interface IItemList {
	mini?: boolean;
	title?: JSX.Element | string;
	route: string; // TODO - one of the routes
	icon: string;
	color?: string;
	onClick?: () => void;
}

// TODO - @MFP remove it from controlled
export const ControlledSidebar = observer(({ onClose }: IControlledSidebarProps) => {
	const { sidebarStore } = useStores<IControlledStore>();
	const { indexRoute } = useRoute();
	const devices = useDevices();

	const adminxRoutes = [
		routes.domainFactors.index(),
		routes.reports.index(),
		routes.advertisers.index(),
		routes.campaignGroups.index(),
		routes.campaigns.index(),
		routes.destinations.index(),
		routes.creativeGroups.index(),
		routes.creatives.index(),
		routes.preferences.index(),
		routes.tests.index(),
		routes.visualizer.index(),
		routes.campaignConfigAudit.index(),
		routes.deals.index(),
		routes.domains.index(),
		routes.jobs.index(),
		routes.tools.tool('cids'),
		routes.keysManagement.index(),
		routes.accounts.index(),
	];
	const bidderRoutes = [
		routes.bidder.campaigns(),
		routes.bidder.updateMaxLossClicks(),
		routes.bidder.models(),
		routes.bidderDomains.index(),
		routes.bidderDomainsStates.index(),
		routes.domainOverlap.index(),
		routes.bidderChangesLogs.index(),
	];

	useEffect(() => {
		if (indexRoute && !devices.isSidebarBurger) {
			sidebarStore.setSidebarOpen(true);
		}
	}, [indexRoute, devices.isSidebarBurger]);

	const ItemList = ({ mini = false, title, route, icon, color, onClick }: IItemList) => {
		const handleClick = () => {
			onClose?.();
			onClick?.();
		};
		const { entityRoute } = useRoute();
		const normalize = (path: string) => path.replace(/\/+$/, ''); // remove trailing slashes
		const split = (path: string) => normalize(path).split('/').filter(Boolean);

		const entityParts = split(entityRoute);
		const routeParts = split(route);

		const isActive = routeParts.length > 1 ? normalize(entityRoute) === normalize(route) : entityParts[0] === routeParts[0];
		return (
			<li className={`${styles.itemWrapper} ${isActive ? styles.activeItem : ''}`} onClick={handleClick}>
				<LinkWithParams to={route} className={styles.itemLink}>
					<div className={styles.item}>
						<Icon
							className={styles.icon}
							isMFP={true}
							color={color}
							size={'18px'}
							// font={activeRoute === route ? IconFonts.Filled : IconFonts.Outlined}
						>
							{icon}
						</Icon>
						{mini ? null : <span className={styles.itemTitle}>{title}</span>}
					</div>
				</LinkWithParams>
			</li>
		);
	};

	const isMiniSidebar = (isHover: boolean) => {
		return (
			(!isHover && sidebarStore.getSidebarMode() === SidebarMode.HOVER) ||
			(!sidebarStore.getIsSideBarOpen() && sidebarStore.getSidebarMode() === SidebarMode.LOCK)
		);
	};

	return (
		<Sidebar
			isOpen={sidebarStore.getIsSideBarOpen()}
			isAnimate={sidebarStore.getIsSideBarAnimate() || sidebarStore.getSidebarMode() === SidebarMode.HOVER}
			mode={sidebarStore.getSidebarMode()}>
			{props => {
				const { isHover } = props;
				const _isMiniSidebar = isMiniSidebar(isHover);
				return (
					<div className={styles.wrapper}>
						<div className={styles.toggler}>
							<Logo text={!_isMiniSidebar ? 'AdminX' : ''} to={routes.general.home()}>
								<AdminxLogo />
							</Logo>
						</div>
						<ul className={`${styles.list} ${styles.fullList}`}>
							<ItemList route={routes.general.home()} icon={'bar-chart-square-02'} title={'Overview'} mini={_isMiniSidebar} />
							<SeparationItemList mini={_isMiniSidebar} title={'Bidder'} icon={'chevron-right'} routeList={bidderRoutes}>
								<React.Fragment>
									<ItemList
										route={routes.bidderAdvertisers.advertisers()}
										icon={'magic-wand-01'}
										title={'Advertisers'}
										mini={_isMiniSidebar}
									/>
									<ItemList
										route={routes.bidder.campaigns()}
										icon={'magic-wand-01'}
										title={'Campaigns'}
										mini={_isMiniSidebar}
									/>
									<ItemList
										route={routes.bidder.updateMaxLossClicks()}
										icon={'sliders-03'}
										title={'Max loss / clicks'}
										mini={_isMiniSidebar}
									/>
									<ItemList route={routes.bidder.models()} icon={'dataflow-04'} title={'Models'} mini={_isMiniSidebar} />
									<ItemList
										route={routes.bidderDomains.index()}
										icon={'at-sign'}
										title={'Domains Manager'}
										mini={_isMiniSidebar}
									/>
									<ItemList
										route={routes.bidderDomainsStates.index()}
										icon={'bar-chart-12'}
										title={'Bidder Domains States'}
										mini={_isMiniSidebar}
									/>
									<ItemList
										route={routes.domainOverlap.index()}
										icon={'intersect-circle'}
										title={'Domain Overlap'}
										mini={_isMiniSidebar}
									/>
									<ItemList
										route={routes.bidderChangesLogs.index()}
										icon={'archive'}
										title={'Bidder Changes Logs'}
										mini={_isMiniSidebar}
									/>
								</React.Fragment>
							</SeparationItemList>
							<SeparationItemList mini={_isMiniSidebar} title={'ADX'} icon={'chevron-right'} routeList={adminxRoutes}>
								<React.Fragment>
									<ItemList
										route={routes.reports.index()}
										icon={'bar-chart-12'}
										title={'Self Service Reports'}
										mini={_isMiniSidebar}
									/>
									<ItemList
										route={routes.advertisers.index()}
										icon={'building-07'}
										title={'Advertisers'}
										mini={_isMiniSidebar}
									/>
									<ItemList
										route={routes.campaignGroups.index()}
										icon={'dataflow-04'}
										title={'Campaign Groups'}
										mini={_isMiniSidebar}
									/>
									<ItemList
										route={routes.campaigns.index()}
										icon={'announcement-01'}
										title={'Campaigns'}
										mini={_isMiniSidebar}
									/>
									<ItemList
										route={routes.destinations.index()}
										icon="link-03"
										title="Destinations"
										mini={_isMiniSidebar}
									/>
									<ItemList
										route={routes.creativeGroups.index()}
										icon={'layers-three-02'}
										title={'Creative Groups'}
										mini={_isMiniSidebar}
									/>
									<ItemList
										route={routes.creatives.index()}
										icon={'image-01'}
										title={'Creatives'}
										mini={_isMiniSidebar}
									/>
									<ItemList
										route={routes.visualizer.index()}
										icon={'monitor-01'}
										title={'Creative Visualizer'}
										mini={_isMiniSidebar}
									/>
									<ItemList
										route={routes.campaignConfigAudit.index()}
										icon={'search-sm'}
										title={'Configuration Audit'}
										mini={_isMiniSidebar}
									/>
									<ItemList
										route={routes.preferences.index()}
										icon={'settings-01'}
										title={'Preferences'}
										mini={_isMiniSidebar}
									/>
									<ItemList route={routes.tests.index()} icon={'paint'} title={'Tests'} mini={_isMiniSidebar} />
									<ItemList route={routes.deals.index()} icon={'wallet-04'} title={'Deals'} mini={_isMiniSidebar} />
									{/* */}
									<SideOpenItemList
										mini={_isMiniSidebar}
										title={'Domains'}
										icon={'chevron-right'}
										routeList={[routes.domains.index(), routes.domainMaxLoss.index()]}>
										<ItemList route={routes.domains.index()} icon={'at-sign'} title={'Report'} mini={_isMiniSidebar} />
										<ItemList
											route={routes.domainMaxLoss.index()}
											icon={'pause-circle'}
											title={'Paused Cache'}
											mini={_isMiniSidebar}
										/>
										<ItemList
											route={routes.domainFactors.index()}
											icon={'lightbulb-01'}
											title={'Factors'}
											mini={_isMiniSidebar}
										/>
									</SideOpenItemList>
									<ItemList route={routes.jobs.index()} icon={'alarm-clock'} title={'Jobs'} mini={_isMiniSidebar} />
									<ItemList route={routes.tools.tool('cids')} icon={'tool-01'} title={'Webapps'} mini={_isMiniSidebar} />
									<ItemList
										route={routes.keysManagement.index()}
										icon={'alarm-clock'}
										title={'Keys Management'}
										mini={_isMiniSidebar}
									/>
									<ItemList route={routes.accounts.index()} icon="users-03" title="Accounts" mini={_isMiniSidebar} />
								</React.Fragment>
							</SeparationItemList>
						</ul>
					</div>
				);
			}}
		</Sidebar>
	);
});
